import { useEffect, useState, type RefObject } from 'react';

import type { SideNavLink } from './types';

import { readCssDurationMs } from '../../../../utils/readCssDurationMs';

export interface PinnedRow {
  isEntering: boolean;
  isLeaving: boolean;
  link: SideNavLink;
}

function haveSameRows(a: PinnedRow[], b: PinnedRow[]) {
  return (
    a.length === b.length &&
    a.every(
      (row, index) =>
        row.link === b[index].link &&
        row.isEntering === b[index].isEntering &&
        row.isLeaving === b[index].isLeaving,
    )
  );
}

/**
 * An unpinned entry holds its place, marked as leaving, until its exit has
 * played. Otherwise the row it sat in is gone on the next paint and the rows
 * below jump up. It stays at the index it had, so the list does not resort
 * around a row that is on its way out.
 *
 * Only a key that was not here before is marked as entering. Reordering moves
 * rows the list already had, so dropping one settles it into its new place
 * instead of replaying every row's arrival.
 */
export function reconcilePinnedRows(
  previous: PinnedRow[],
  links: SideNavLink[],
) {
  const known = new Set(previous.map((row) => row.link.key));
  const pinned = new Set(links.map((link) => link.key));
  const next: PinnedRow[] = links.map((link) => ({
    isEntering: !known.has(link.key),
    isLeaving: false,
    link,
  }));

  previous.forEach((row, index) => {
    if (pinned.has(row.link.key)) {
      return;
    }
    next.splice(Math.min(index, next.length), 0, {
      ...row,
      isEntering: false,
      isLeaving: true,
    });
  });

  return haveSameRows(previous, next) ? previous : next;
}

/**
 * The rows to render, including the ones animating out, and whether the section
 * itself is on its way out. Unpinning the last entry plays the section's exit
 * instead of snapping it away mid-animation.
 */
export function usePinnedSectionPresence(
  links: SideNavLink[],
  sectionRef: RefObject<HTMLElement | null>,
) {
  // Rows already pinned when the section mounts arrive with it, so they do not
  // animate on their own. The section's own entrance covers them.
  const [rows, setRows] = useState<PinnedRow[]>(() =>
    links.map((link) => ({ isEntering: false, isLeaving: false, link })),
  );

  useEffect(() => {
    setRows((previous) => reconcilePinnedRows(previous, links));
  }, [links]);

  useEffect(() => {
    if (!rows.some((row) => row.isEntering)) {
      return;
    }

    const timeout = setTimeout(
      () =>
        setRows((previous) =>
          previous.map((row) =>
            row.isEntering ? { ...row, isEntering: false } : row,
          ),
        ),
      readCssDurationMs(sectionRef.current, '--_reveal-row-enter'),
    );
    return () => clearTimeout(timeout);
  }, [rows, sectionRef]);

  useEffect(() => {
    if (!rows.some((row) => row.isLeaving)) {
      return;
    }

    const timeout = setTimeout(
      () => setRows((previous) => previous.filter((row) => !row.isLeaving)),
      // The section's exit is the longer of the two, so a row waiting on it
      // outlives its own.
      readCssDurationMs(sectionRef.current, '--_reveal-exit'),
    );
    return () => clearTimeout(timeout);
  }, [rows, sectionRef]);

  return { isLeaving: links.length === 0 && rows.length > 0, rows };
}
